import React, {useState, useEffect} from "react";
import {useNavigate, useLocation} from "react-router";
import Registration from "./Registration.jsx";
import FormLayout from "../FormLayout";
import {
  getMessageObject,
  MessageType,
} from "../FormLayout/MessageFactory";
import {
  validateUsername,
  validatePassword,
  validateForm
} from "../validation";
import { inject, observer } from "mobx-react";

const RegistrationContainer = (props) => {

  const {userStore} = props;
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const status = parseInt(params.get('confirmationStatus') || 0, 10) || 0;

  const {message = null} = location.state || {};
  const [formErrors, setFormErrors] = useState({});
  const [username, setUsername] = useState(location.state?.username);
  const [password, setPassword] = useState(location.state?.password);
  const [passwordCheck, setPasswordCheck] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [messageObject, setMessageObject] = useState(
    status === 0
    ? message
    : status === 1
      ? getMessageObject(MessageType.UserNotFoundException, username)
      : status === 2
        ? getMessageObject(MessageType.UserNotConfirmedException)
        : status === 3
          ? getMessageObject(MessageType.LinkExpired, username)
          : status === 4
            ? getMessageObject(MessageType.ConfirmationFailed, username)
            : status === 5
              ? getMessageObject(MessageType.NotAuthorized, username)
              : null
  );
  const [showWarnings, setShowWarnings] = useState({
                                                     username: username !== "",
                                                     password: password !== "",
                                                     passwordCheck: false,
                                                     acceptTerms: false
                                                   });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [validating, setValidating] = useState(false);
  const [passwordInvalid, setPasswordInvalid] = useState(location.state?.password ? false : null);

  const resendConfirmationCode = (message = null, password = null) => {
    userStore
      .resendConfirmation(username)
      .then(result => {
        navigate("/verify", {
          replace: true,
          state: {
            username,
            password,
            message: message || getMessageObject(MessageType.ConfirmAccount, username)
          },
        });
      })
      .catch(error => {
        if (error.name === "UserNotFoundException") {
          navigate("/register", {
            replace: true,
            state: {
              username,
              password,
              message: {message: "Please register", type: "warning"},
            }
          });
        } else if (error.name === "LimitExceededException") {
          navigate("/signin", {
            replace: true,
            state: {
              username,
              message: {message: "Too many retries, try again later", type: "error"},
            },
          });
        } else if (error.name === "InvalidParameterException") {
          // This may happen if the user is already confirmed and tries to re-use a confirmation link
          // Track the error just in case there's some other reason
          navigate("/signin", {
            replace: true,
            state: {
              username: username,
              password: password || "",
              message: getMessageObject(MessageType.AlreadyConfirmed, username)
            },
          });
        } else {
          setMessageObject({message: error?.message || "<Unknown error>", type: "error", name: error.name});
        }
      });
  };

  const handleRegistration = () => {
    // PLEX-1566, PLEX-2340: Show all validation errors on submit
    setShowWarnings({
      username: true,
      password: true,
      passwordCheck: true,
      acceptTerms: true
    });

    // Check if there are any validation errors before submitting
    const errors = validateForm(username, password, passwordCheck);
    if (!acceptTerms) {
      errors.acceptTerms = 'You must accept the terms of service';
    }

    if (errors.submit || errors.acceptTerms) {
      // Don't submit if there are errors - the useEffect will display them
      return;
    }

    // Clear all error messages and warnings on successful submit
    setFormSubmitted(true);
    setMessageObject(null);
    setShowWarnings({
      username: false,
      password: false,
      passwordCheck: false,
      acceptTerms: false
    });
    userStore
      .register(username, password)
      .then(result => {
        navigate("/verify", {
          replace: true,
          state: {
            username: username,
            password: password,
            message: getMessageObject(MessageType.NewConfirmationCode, username)
          }
        });
      })
      .catch(error => {
        // PLEX-2937, PLEX-1206: Handle username already exists errors (multiple possible error names)
        if (error.name === "UsernameExistsException" || error.name === "UsernameAlreadyExists") {
          // PLEX-1463: The user is either unconfirmed or already registered
          // Attempt a confirmation re-send to determine which case:
          // - If unconfirmed: resend succeeds, navigate to /verify
          // - If confirmed: resend fails with InvalidParameterException, navigate to /signin
          resendConfirmationCode(getMessageObject(MessageType.NewConfirmationCode, username), password);
        } else {
          let errorMsg = error?.message;
          if (error.name === "UserLambdaValidationException") {
            if (errorMsg.includes("service-unavailable")) {
              setFormSubmitted(false);
              setMessageObject(getMessageObject(MessageType.ServiceUnavailable, username));
              return;
            }
            if (errorMsg.indexOf("unauthorized") === -1) {
              errorMsg = `The user ${username} is not authorized to use this system`;
            }
            else {
              errorMsg = null;
            }
          }
          // NOTE: error for registered and unconfirmed users is the same
          setFormSubmitted(false);
          setMessageObject(getMessageObject(MessageType.NotAuthorized, errorMsg));
        }
      });
  };

  const handleChange = field => evt => {
    setMessageObject(null);
    if (field === "acceptTerms") {
      setAcceptTerms(evt.target.checked);
      setShowWarnings({ ...showWarnings, acceptTerms: true });
    } else {
      const value = evt.target.value;
      if (field === "username") {
        setUsername(value.trim());
      }
      else if (field === "password") {
        setPassword(value);

        // Only call password validation if username is valid
        const usernameError = validateUsername(username);
        if (usernameError) {
          // Don't call API if username is invalid - just clear any previous validation error
          setPasswordInvalid(false);
          return;
        }

        setValidating(true);
        userStore
          .runServerPasswordValidation(username, value, false)
          .then(() => {
            setPasswordInvalid(false);
          })
          .catch(error => {
            const errorContent = error?.content || error?.message;
            const isPasswordReused = errorContent === "PasswordReused";

            if (!isPasswordReused && window.Sentry) {
              window.Sentry.withScope(scope => {
                scope.setContext('password_validation', {
                  username: username,
                  flow: 'registration',
                  error_content: errorContent,
                  error_name: error?.name,
                  error_status: error?.status,
                  error_message: error?.message,
                  full_error: JSON.stringify(error, Object.getOwnPropertyNames(error))
                });
                scope.setLevel('warning');
                scope.setTag('validation_type', 'password');
                scope.setTag('auth_flow', 'registration');
                window.Sentry.captureException(
                  new Error(`Password validation failed: ${errorContent || 'Unknown error'}`)
                );
              });
            }

            if (isPasswordReused) {
              setPasswordInvalid("That password has been recently used");
              setShowWarnings(prev => ({ ...prev, password: true }));
            } else {
              // Server error — don't block submit, just warn
              setPasswordInvalid(false);
              setMessageObject({message: "The new password could not be validated, please try again later", type: "warning"});
            }
          })
          .finally(() => {
            setValidating(false);
          });
      }
      else if (field === "passwordCheck") {
        setPasswordCheck(value);
      }
    }
  };

  // Install this as an onFocus handler if you want any error messages to be cleared
  // when the user is using the field
  const handleFocus = (field, focused) => evt => {
    setShowWarnings({ ...showWarnings, [field]: !focused });
  };

  useEffect(() => {
    const errors = validateForm(username, password, passwordCheck);

    // Add acceptTerms validation
    if (!acceptTerms) {
      errors.acceptTerms = 'You must accept the terms of service';
    }

    // Add server password validation error
    if (passwordInvalid) {
      errors.password = errors.password
        ? errors.password + "\n" + passwordInvalid
        : passwordInvalid;
    }

    // Block submit if server validation is in progress or password is known reused
    errors.submit = errors.submit || passwordInvalid !== false || validating;

    const errorMsg = {
      type: "error",
      name: "error",
      message: ""
    };
    ['username', 'password', 'passwordCheck', 'acceptTerms'].forEach(x => {
      if (showWarnings[x] && errors[x]) {
        errorMsg.message += (errorMsg.message ? "\n" : "") + errors[x];
      }
    });
    setMessageObject(errorMsg?.message ? errorMsg : messageObject);
    setFormErrors(errors);
  }, [username, password, passwordCheck, acceptTerms, passwordInvalid, validating]);

  // ??
  window.name = "plex";
  return (
    <FormLayout message={messageObject}>
      <Registration
        errors={formErrors}
        username={username}
        password={password}
        passwordCheck={passwordCheck}
        acceptTerms={acceptTerms}
        onChange={handleChange}
        onRegister={handleRegistration}
        onSwitchToSignin={() => navigate("/signin", {
          replace: true,
          state: {
            username,
            password,
          }
        })}
        onFocus={handleFocus}
        formSubmitted={formSubmitted}
        validating={validating}
      />
    </FormLayout>
  );
};

export default inject("userStore")(observer(RegistrationContainer));
export { validatePassword, validateUsername, validateForm };
